<template>
  <div>
    <header ref="headerRef" class="fixed inset-x-0 top-0 z-40 w-full border-b border-stone-200/70 bg-white/90 backdrop-blur">
      <div class="mx-auto hidden max-w-7xl items-center justify-end px-4 pt-3 text-sm text-stone-500 md:flex">
        <div class="flex items-center gap-2">
          <span>{{ t("lang") }}</span>
          <button
          v-for="lang in ['th', 'en']"
          :key="lang"
          class="rounded-full border px-2 py-1 uppercase"
          :class="locale === lang ? 'border-brand-500 bg-brand-50 text-brand-600' : 'border-stone-200 hover:border-brand-500 hover:text-brand-600'"
          @click="setLocale(lang)"
        >
          {{ lang }}
        </button>
      </div>
      </div>

      <div class="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 md:py-4">
        <RouterLink to="/" class="flex shrink-0 items-center gap-3">
          <div class="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-orange-400 text-lg font-semibold text-white shadow-float">
            ฐ
          </div>
          <div>
            <div class="text-lg font-semibold leading-none text-stone-900">ฐิติมา พาณิชย์</div>
            <div class="text-xs text-stone-500">Fire Safety and Office Supply</div>
          </div>
        </RouterLink>

        <div class="hidden min-w-0 flex-1 md:block" v-if="showSearch">
          <form @submit.prevent class="flex overflow-hidden rounded-2xl border-2 border-brand-500 bg-white shadow-sm">
            <input
              :value="searchQuery"
              type="text"
              class="min-w-0 flex-1 px-4 py-3 outline-none"
              :placeholder="t('searchPlaceholder')"
              @input="$emit('update:searchQuery', $event.target.value)"
            />
            <button type="submit" class="bg-brand-500 px-5 text-white transition hover:bg-brand-600">{{ t("search") }}</button>
          </form>
        </div>

        <nav class="hidden items-center gap-1 text-[15px] text-stone-600 md:ml-auto md:flex">
          <component
            :is="item.to ? RouterLink : 'a'"
            v-for="item in localizedNavItems"
            :key="item.key"
            :to="item.to"
            :href="item.href"
            class="rounded-full px-3 py-2 transition"
            :class="isActiveNav(item) ? 'bg-brand-50 font-medium text-brand-600' : 'text-stone-600 hover:text-brand-600'"
          >
            {{ item.label }}
          </component>
        </nav>

        <button
          type="button"
          class="ml-auto rounded-2xl border border-stone-200 p-3 text-stone-700 md:hidden"
          :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
          aria-controls="mobile-site-menu"
          aria-label="Toggle navigation menu"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          ☰
        </button>
      </div>

      <div v-if="mobileMenuOpen" id="mobile-site-menu" class="border-t border-stone-200 bg-white md:hidden">
        <div class="space-y-2 px-4 py-4">
          <component
            :is="item.to ? RouterLink : 'a'"
            v-for="item in localizedNavItems"
            :key="item.key"
            :to="item.to"
            :href="item.href"
            class="block rounded-xl px-3 py-2 transition"
            :class="isActiveNav(item) ? 'bg-brand-50 font-medium text-brand-600' : 'text-stone-700 hover:bg-brand-50'"
            @click="mobileMenuOpen = false"
          >
            {{ item.label }}
          </component>
        </div>
      </div>
    </header>
    <div aria-hidden="true" class="shrink-0" :style="{ height: `${headerHeight}px` }" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

const props = defineProps({
  navItems: {
    type: Array,
    default: () => []
  },
  searchQuery: {
    type: String,
    default: ""
  },
  showSearch: {
    type: Boolean,
    default: true
  }
});

defineEmits(["update:searchQuery"]);

const route = useRoute();
const headerRef = ref(null);
const headerHeight = ref(72);
const mobileMenuOpen = ref(false);
const currentHash = ref(typeof window !== "undefined" ? window.location.hash : "");
const { locale, t } = useI18n();
let scrollTicking = false;

const localizedNavItems = computed(() =>
  props.navItems.map((item) => ({
    ...item,
    label: item.label?.[locale.value] || item.label?.th || ""
  }))
);

const syncHash = () => {
  currentHash.value = typeof window !== "undefined" ? window.location.hash : "";
};

const getSectionHashes = () =>
  props.navItems
    .map((item) => {
      if (item.href?.startsWith("/#")) return item.href.replace("/#", "#");
      if (item.href?.startsWith("#")) return item.href;
      return null;
    })
    .filter(Boolean);

const syncActiveSection = () => {
  if (route.path !== "/" || typeof window === "undefined") {
    syncHash();
    return;
  }

  const sectionHashes = getSectionHashes();
  if (!sectionHashes.length) {
    syncHash();
    return;
  }

  const scrollY = window.scrollY;
  if (scrollY < headerHeight.value) {
    currentHash.value = "";
    return;
  }

  let activeHash = "";
  const offset = headerHeight.value + 24;

  sectionHashes.forEach((hash) => {
    const section = document.querySelector(hash);
    if (!section) return;

    const top = section.getBoundingClientRect().top + window.scrollY;
    if (scrollY >= top - offset) {
      activeHash = hash;
    }
  });

  currentHash.value = activeHash;
};

const handleScroll = () => {
  if (scrollTicking) return;
  scrollTicking = true;
  window.requestAnimationFrame(() => {
    syncActiveSection();
    scrollTicking = false;
  });
};

const isActiveNav = (item) => {
  if (item.to) {
    if (item.to === "/") {
      return route.path === "/" && !currentHash.value;
    }
    return route.path === item.to;
  }

  if (item.href?.startsWith("/#")) {
    const targetHash = item.href.replace("/#", "#");
    return route.path === "/" && currentHash.value === targetHash;
  }

  if (item.href?.startsWith("#")) {
    return route.path === "/" && currentHash.value === item.href;
  }

  return false;
};

let headerResizeObserver = null;

const updateHeaderHeight = () => {
  if (!headerRef.value) return;
  const height = headerRef.value.offsetHeight;
  headerHeight.value = height;
  document.documentElement.style.setProperty("--site-header-height", `${height}px`);
};

const setLocale = (lang) => {
  locale.value = lang;
  localStorage.setItem("ucanbuy-locale", lang);
  document.documentElement.lang = lang;
  if (route.name === "about") {
    document.title = lang === "th" ? "เกี่ยวกับเรา | ฐิติมา พาณิชย์" : "About Thitima Panich";
    return;
  }
  document.title =
    lang === "th" ? "ฐิติมา พาณิชย์ | อุปกรณ์ดับเพลิง เซฟตี้ และวัสดุสำนักงาน" : "Thitima Panich | Fire Safety and Office Supply";
};

onMounted(() => {
  updateHeaderHeight();
  if (headerRef.value && typeof ResizeObserver !== "undefined") {
    headerResizeObserver = new ResizeObserver(updateHeaderHeight);
    headerResizeObserver.observe(headerRef.value);
  }
  syncActiveSection();
  window.addEventListener("hashchange", syncHash);
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", updateHeaderHeight);
});

onBeforeUnmount(() => {
  headerResizeObserver?.disconnect();
  window.removeEventListener("hashchange", syncHash);
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", updateHeaderHeight);
});

watch(mobileMenuOpen, () => {
  requestAnimationFrame(updateHeaderHeight);
});

watch(
  () => route.fullPath,
  () => {
    mobileMenuOpen.value = false;
    syncActiveSection();
  }
);
</script>
